'use client';

import { UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { formatBulkCount } from '@/components/app/bulk-delete';
import {
  AppDialogBody,
  AppDialogFooter,
  AppDialogHeader,
  AppDialogRoot,
} from '@/components/ui/app-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useAssignPractitionerServices,
  useListPractitioners,
} from '@/features/practitioners';
import { cn } from '@/lib/utils';
import type { PractitionerWithRelations } from '@borradh-workspace/api-client/types';

import {
  buildBulkServiceAssignments,
  countAlreadyAssigned,
} from './assign-services-to-team';

const NOUN = { singular: 'service', plural: 'services' };

/** How many failed names the summary toast spells out before "and N more". */
const MAX_NAMED_FAILURES = 3;

export interface AssignServicesToTeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The selected services. The name is only read when there is exactly one. */
  services: ReadonlyArray<{ id: string; name: string }>;
  /** Every chosen team member was updated — the page clears its selection. */
  onAssigned?: () => void;
}

/**
 * "Assign to team" for a bulk selection of services.
 *
 * The services page lists SERVICES, so its selection is a set of services and
 * the natural question is "who should offer these?". Each ticked team member
 * gets every selected service ADDED to what they already offer — see
 * `buildBulkServiceAssignments` for why it must be additive — and anyone who
 * already offers all of them is greyed with "Already assigned" rather than
 * hidden, so the operator can see the assignment has happened instead of
 * wondering why a name is missing.
 *
 * Writes go one request per team member through the same endpoint the service
 * form and team-member editor use, and the dialog raises ONE summary toast; a
 * member whose write failed stays ticked with the dialog open, so what is left
 * is exactly what still needs doing.
 */
export function AssignServicesToTeamDialog({
  open,
  onOpenChange,
  services,
  onAssigned,
}: AssignServicesToTeamDialogProps) {
  // Team management, not booking: invitees stay listed, as they do on the
  // service form, because pre-assigning a new hire's services is the point.
  const { practitioners, isLoading, isError, error, refetch } =
    useListPractitioners({ params: { limit: 100 } });
  const { assignServicesAsync } = useAssignPractitionerServices({
    showSuccessToast: false,
    showErrorToast: false,
  });

  const serviceIds = useMemo(() => services.map((s) => s.id), [services]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isAssigning, setIsAssigning] = useState(false);

  // A selection belongs to one visit. The dialog stays mounted while closed,
  // so without this a second open would reopen with the previous tick marks —
  // possibly on members who now read "Already assigned". Adjusted DURING
  // render, so the stale ticks are never painted.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    setSelected(new Set());
  }

  const rows = useMemo(
    () =>
      practitioners.map((practitioner) => {
        const already = countAlreadyAssigned(practitioner, serviceIds);
        return {
          practitioner,
          already,
          complete: serviceIds.length > 0 && already === serviceIds.length,
        };
      }),
    [practitioners, serviceIds]
  );
  const assignable = useMemo(() => rows.filter((row) => !row.complete), [rows]);
  const allSelected =
    assignable.length > 0 &&
    assignable.every((row) => selected.has(row.practitioner.id));

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const toggleAll = () =>
    setSelected(
      allSelected
        ? new Set()
        : new Set(assignable.map((row) => row.practitioner.id))
    );

  const count = selected.size;
  const serviceCount = formatBulkCount(serviceIds.length, NOUN);

  const handleAssign = async () => {
    setIsAssigning(true);
    // The team list is cached for a minute, and PUT replaces a member's whole
    // set — so a union built from a stale list would drop whatever another
    // admin assigned since. Refresh first and build from THAT; the cached
    // list is only the fallback if the refresh itself fails.
    let team = practitioners;
    try {
      const fresh = await refetch();
      team = fresh?.data?.items ?? practitioners;
    } catch {
      // Offline or the API hiccupped: the cached list is the best we have.
    }

    const assignments = buildBulkServiceAssignments({
      serviceIds,
      practitioners: team,
      selectedPractitionerIds: [...selected],
    });
    if (assignments.length === 0) {
      // Everyone ticked already offers everything selected.
      setIsAssigning(false);
      onAssigned?.();
      onOpenChange(false);
      return;
    }

    // One request per team member: the endpoint is per-practitioner, and a
    // failure for one must not roll back the others.
    const results = await Promise.allSettled(
      assignments.map((assignment) => assignServicesAsync(assignment))
    );
    setIsAssigning(false);

    const failed: { id: string; error: Error }[] = [];
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        failed.push({
          id: assignments[index].practitionerId,
          error: toError(result.reason),
        });
      }
    });
    const succeeded = assignments.length - failed.length;
    const nameOf = (id: string) => practitioners.find((p) => p.id === id)?.name;

    if (succeeded > 0) {
      const to =
        succeeded === 1
          ? (nameOf(
              assignments.find(
                (a) => !failed.some((f) => f.id === a.practitionerId)
              )?.practitionerId ?? ''
            ) ?? '1 team member')
          : `${succeeded} team members`;
      toast.success(`${serviceCount} assigned to ${to}`);
    }

    if (failed.length === 0) {
      setSelected(new Set());
      onAssigned?.();
      onOpenChange(false);
      return;
    }

    // Keep ONLY the failures ticked: what succeeded is done, and what is left
    // is exactly what still needs attention.
    setSelected(new Set(failed.map(({ id }) => id)));
    toast.error(
      `Couldn't assign to ${
        failed.length === 1 ? '1 team member' : `${failed.length} team members`
      }`,
      { description: describeFailures(failed, nameOf) }
    );
  };

  return (
    <AppDialogRoot onOpenChange={onOpenChange} open={open} size="md">
      <AppDialogHeader
        description={
          serviceIds.length === 1
            ? `Adds “${services[0]?.name}” to the team members you pick. Anything they already offer stays.`
            : `Adds ${serviceCount} to the team members you pick. Anything they already offer stays.`
        }
        icon={UserPlus}
        title="Assign to team"
      />

      <AppDialogBody className="flex max-h-[60vh] flex-col gap-3 overflow-hidden p-0">
        {assignable.length > 0 && !(isLoading || isError) && (
          <div className="flex items-center justify-between px-6 pt-5">
            <Button
              className="h-auto p-0 text-sm"
              onClick={toggleAll}
              variant="link"
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </Button>
            <span className="text-muted-foreground text-sm">
              {rows.length} {rows.length === 1 ? 'team member' : 'team members'}
            </span>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-1">
          {isLoading && (
            <div className="flex flex-col gap-2 pt-5">
              {Array.from({ length: 4 }, (_, index) => (
                <Skeleton className="h-12 w-full" key={index} />
              ))}
            </div>
          )}

          {!isLoading && isError && (
            <div className="flex flex-col items-center gap-3 py-10">
              <p className="text-destructive text-sm" role="alert">
                {error?.message ?? 'We could not load your team.'}
              </p>
              <Button onClick={() => void refetch()} variant="outline">
                Try again
              </Button>
            </div>
          )}

          {!(isLoading || isError) && rows.length === 0 && (
            <p className="py-10 text-center text-muted-foreground text-sm">
              No team members yet. Add someone to your team first.
            </p>
          )}

          {!(isLoading || isError) && rows.length > 0 && (
            <ul className="flex flex-col gap-1">
              {rows.map(({ practitioner, already, complete }) => (
                <li key={practitioner.id}>
                  <TeamMemberRow
                    already={already}
                    checked={selected.has(practitioner.id)}
                    complete={complete}
                    onToggle={() => toggle(practitioner.id)}
                    practitioner={practitioner}
                    total={serviceIds.length}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </AppDialogBody>

      <AppDialogFooter className="sm:justify-between">
        <span className="text-muted-foreground text-sm">{count} selected</span>
        <div className="flex items-center gap-2">
          <Button
            disabled={isAssigning}
            onClick={() => onOpenChange(false)}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            disabled={count === 0 || isAssigning}
            onClick={() => void handleAssign()}
          >
            {isAssigning ? 'Assigning…' : `Assign ${serviceCount}`}
          </Button>
        </div>
      </AppDialogFooter>
    </AppDialogRoot>
  );
}

function TeamMemberRow({
  practitioner,
  already,
  total,
  complete,
  checked,
  onToggle,
}: {
  practitioner: PractitionerWithRelations;
  already: number;
  total: number;
  complete: boolean;
  checked: boolean;
  onToggle: () => void;
}) {
  const inputId = `assign-team-${practitioner.id}`;
  const initials = practitioner.name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/50',
        // Someone who already offers everything stays VISIBLE, greyed, rather
        // than filtered out — see the component note.
        complete && 'cursor-default hover:bg-transparent'
      )}
      htmlFor={inputId}
    >
      <Checkbox
        checked={checked}
        disabled={complete}
        id={inputId}
        onCheckedChange={onToggle}
      />
      <Avatar className="size-9">
        {practitioner.photo && (
          <AvatarImage alt={practitioner.name} src={practitioner.photo} />
        )}
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-sm',
          complete && 'text-muted-foreground'
        )}
      >
        {practitioner.name}
      </span>
      <span className="shrink-0 text-muted-foreground text-sm">
        {complete
          ? 'Already assigned'
          : already > 0
            ? `Has ${already} of ${total}`
            : null}
      </span>
    </label>
  );
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function describeFailures(
  failed: { id: string; error: Error }[],
  nameOf: (id: string) => string | undefined
): string {
  const names = failed
    .map(({ id }) => nameOf(id))
    .filter((name): name is string => !!name);
  const parts: string[] = [];
  if (names.length > 0) {
    const shown = names.slice(0, MAX_NAMED_FAILURES).map((n) => `“${n}”`);
    const rest = failed.length - shown.length;
    parts.push(
      `${shown.join(', ')}${rest > 0 ? ` and ${rest} more` : ''} ${
        failed.length === 1 ? 'is' : 'are'
      } still selected.`
    );
  } else {
    parts.push(`${failed.length === 1 ? 'It is' : 'They are'} still selected.`);
  }
  // The server's own reason, not a generic "failed": it is usually the one
  // thing the operator needs to read.
  const reason = failed[0]?.error.message;
  if (reason) parts.push(reason);
  return parts.join(' ');
}
